import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Droplets, LogOut, Package, Truck, DollarSign, CheckCircle2, Loader2,
  UserPlus, X, Users, RefreshCw, Waves, Warehouse, HardHat, Siren, Ban, RotateCcw, Wallet, Fuel, PlusCircle,
} from "lucide-react";
import { ManualOrderModal } from "../components/ManualOrderModal";
import { CustomersPanel } from "../components/CustomersPanel";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import ServiceAreaManager from "@/components/ServiceAreaManager";
import CouponsPanel from "@/components/CouponsPanel";

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"}`}>
    {children}
  </button>
);

const SERVICE_ICON = { pool: Waves, tank: Warehouse, construction: HardHat, emergency: Siren };
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });
};
const STATUS_STYLE = {
  new: "bg-slate-100 text-slate-600",
  assigned: "bg-blue-100 text-blue-700",
  in_transit: "bg-amber-100 text-amber-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-600",
};
const STATUS_LABEL = { new: "New", assigned: "Assigned", in_transit: "In Transit", delivered: "Delivered", cancelled: "Cancelled" };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDriver, setShowDriver] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [autoAssign, setAutoAssign] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [tab, setTab] = useState("orders");

  const loadAll = useCallback(async () => {
    try {
      const [m, o, d, aa] = await Promise.all([
        api.get("/admin/metrics"), api.get("/admin/orders"), api.get("/admin/drivers"), api.get("/admin/auto-assign"),
      ]);
      setMetrics(m.data); setOrders(o.data); setDrivers(d.data); setAutoAssign(aa.data.driver_id || "");
    } catch (e) {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const assign = async (orderId, driverId) => {
    if (!driverId) return;
    try {
      await api.patch(`/admin/orders/${orderId}/assign`, { driver_id: driverId });
      toast.success("Load assigned to driver");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const cancelOrder = async (orderId, resolution) => {
    try {
      await api.post(`/admin/orders/${orderId}/cancel`, { resolution });
      toast.success(resolution === "refund" ? "Order cancelled & refunded" : resolution === "credit" ? "Order cancelled — store credit issued" : "Order cancelled");
      setCancelTarget(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const saveAutoAssign = async (driverId) => {
    setAutoAssign(driverId);
    try {
      await api.patch("/admin/auto-assign", { driver_id: driverId || null });
      toast.success(driverId ? "New orders will auto-assign to this driver" : "New orders will balance across drivers");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadAll(); }
  };

  if (loading)
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  const stats = [
    { label: "Total Orders", value: metrics.total_orders, icon: Package, color: "bg-blue-50 text-blue-600" },
    { label: "Delivered", value: metrics.delivered, icon: CheckCircle2, color: "bg-green-50 text-green-600" },
    { label: "Unassigned", value: metrics.unassigned, icon: Truck, color: "bg-amber-50 text-amber-600" },
    { label: "Revenue", value: `$${metrics.revenue.toLocaleString()}`, icon: DollarSign, color: "bg-cyan-50 text-cyan-600" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline text-xs font-semibold bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} className="p-2 rounded-lg hover:bg-slate-100" data-testid="admin-refresh-btn"><RefreshCw className="w-5 h-5 text-slate-500" /></button>
            <span className="hidden sm:inline text-sm text-slate-500">{user?.name}</span>
            <button onClick={doLogout} className="p-2 rounded-lg hover:bg-slate-100 flex items-center gap-1 text-sm text-slate-600" data-testid="admin-logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-2 mb-6">
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="admin-tab-orders">Orders & Drivers</TabBtn>
          <TabBtn active={tab === "areas"} onClick={() => setTab("areas")} testid="admin-tab-areas">Service Areas & Pricing</TabBtn>
          <TabBtn active={tab === "coupons"} onClick={() => setTab("coupons")} testid="admin-tab-coupons">Coupons & Stock</TabBtn>
          <TabBtn active={tab === "customers"} onClick={() => setTab("customers")} testid="admin-tab-customers">Customers</TabBtn>
        </div>

        {tab === "areas" && <ServiceAreaManager />}
        {tab === "coupons" && <CouponsPanel />}
        {tab === "customers" && <CustomersPanel />}

        {tab === "orders" && (<>
        {/* METRICS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-5 border border-slate-100" data-testid={`metric-${s.label.toLowerCase().replace(" ", "-")}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div className="font-display text-2xl font-extrabold text-slate-900">{s.value}</div>
              <div className="text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
          {/* ORDERS TABLE */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                <h2 className="font-heading font-bold text-slate-900">All Orders</h2>
              </div>
              <button onClick={() => setShowManual(true)} data-testid="admin-new-load-btn"
                className="text-sm font-semibold brand-gradient text-white px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5">
                <PlusCircle className="w-4 h-4" /> New Load
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                    <th className="px-6 py-3 font-semibold">Customer</th>
                    <th className="px-4 py-3 font-semibold">Location</th>
                    <th className="px-4 py-3 font-semibold">Delivery</th>
                    <th className="px-4 py-3 font-semibold">Water</th>
                    <th className="px-4 py-3 font-semibold">Payment</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Driver</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const Icon = SERVICE_ICON[o.service_type] || Droplets;
                    return (
                      <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`admin-order-${o.id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-slate-400" />
                            <div>
                              <div className="font-semibold text-slate-800">{o.customer_name}</div>
                              <div className="text-xs text-slate-400">{o.customer_phone}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          <div>{o.suburb}</div>
                          {o.filling_station && (
                            <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5" data-testid={`admin-order-station-${o.id}`}>
                              <Fuel className="w-3 h-3 shrink-0" /> {o.filling_station.split(" — ")[0]}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-medium text-slate-700" data-testid={`admin-order-date-${o.id}`}>{fmtDate(o.scheduled_date)}</div>
                          <div className="text-xs text-slate-400">{o.loads} load{o.loads > 1 ? "s" : ""}</div>
                          {o.hose_length != null && (
                            <div className="text-xs text-slate-400" data-testid={`admin-order-hose-${o.id}`}>
                              Hose {o.hose_length}m{o.hose_fee > 0 ? ` (+$${o.hose_fee})` : ""}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 font-semibold text-slate-800">{o.water_type === "pure" ? "Pure" : "Town"}</td>
                        <td className="px-4 py-4">
                          <div className="text-slate-800">${o.amount}</div>
                          <div className={`text-xs ${o.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>
                            {o.payment_method === "card" ? (o.payment_status === "paid" ? "Paid" : "Card pending") : "On delivery"}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[o.order_status]}`}>
                            {STATUS_LABEL[o.order_status]}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          {o.order_status === "cancelled" ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : o.order_status === "delivered" ? (
                            <span className="text-xs text-slate-400">{o.driver_name}</span>
                          ) : (
                            <select
                              value={o.driver_id || ""}
                              onChange={(e) => assign(o.id, e.target.value)}
                              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-blue-500 bg-white"
                              data-testid={`admin-assign-driver-select-${o.id}`}
                            >
                              <option value="">Assign...</option>
                              {drivers.map((d) => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right">
                          {o.order_status === "cancelled" ? (
                            <span className="text-xs font-semibold text-red-500" data-testid={`admin-order-cancelled-${o.id}`}>
                              {o.cancel_resolution === "refund" ? "Refunded" : o.cancel_resolution === "credit" ? "Credited" : "Cancelled"}
                            </span>
                          ) : o.order_status === "delivered" ? (
                            <span className="text-xs text-slate-300">—</span>
                          ) : (
                            <button onClick={() => setCancelTarget(o)} data-testid={`admin-cancel-order-${o.id}`}
                              className="text-xs font-semibold text-red-600 hover:bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1">
                              <Ban className="w-3.5 h-3.5" /> Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {orders.length === 0 && <div className="p-10 text-center text-slate-400">No orders yet.</div>}
            </div>
          </div>

          {/* DRIVERS */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" /> Drivers
              </h2>
              <button onClick={() => setShowDriver(true)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg" data-testid="admin-add-driver-btn">
                <UserPlus className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Auto-assign new orders to</label>
              <select value={autoAssign} onChange={(e) => saveAutoAssign(e.target.value)} data-testid="admin-auto-assign-select"
                className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2 outline-none focus:border-blue-500 bg-white">
                <option value="">Balance across all drivers</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="space-y-3">
              {drivers.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-slate-100 rounded-xl p-3" data-testid={`admin-driver-${d.id}`}>
                  <div>
                    <div className="font-semibold text-slate-800 text-sm">{d.name}</div>
                    <div className="text-xs text-slate-400">{d.phone || d.email}</div>
                  </div>
                  <span className="text-xs font-semibold bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full">{d.active_loads} active</span>
                </div>
              ))}
              {drivers.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No drivers yet.</p>}
            </div>
          </div>
        </div>
        </>)}
      </div>

      {showDriver && <AddDriverModal onClose={() => setShowDriver(false)} onDone={() => { setShowDriver(false); loadAll(); }} />}
      {cancelTarget && <CancelOrderModal order={cancelTarget} onClose={() => setCancelTarget(null)} onConfirm={cancelOrder} />}
      {showManual && <ManualOrderModal onClose={() => setShowManual(false)} onCreated={loadAll} />}
    </div>
  );
}

function CancelOrderModal({ order, onClose, onConfirm }) {
  const paid = order.payment_status === "paid";
  const hasAccount = !!order.user_id;
  const [choice, setChoice] = useState(paid ? (hasAccount ? "refund" : "refund") : "none");
  const [loading, setLoading] = useState(false);

  const confirm = async () => {
    setLoading(true);
    await onConfirm(order.id, paid ? choice : "none");
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-5" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="cancel-order-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><Ban className="w-5 h-5 text-red-500" /> Cancel order</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 mb-4 text-sm">
          <div className="font-semibold text-slate-800">{order.customer_name}</div>
          <div className="text-slate-500">{order.suburb} · {order.loads} load(s) · <span className="font-semibold text-slate-700">${order.amount}</span></div>
          <div className={`text-xs mt-1 ${paid ? "text-green-600" : "text-amber-600"}`}>{paid ? "Customer has paid" : "Not paid yet"}</div>
        </div>

        {paid ? (
          <div className="space-y-2 mb-5">
            <p className="text-sm text-slate-600 mb-2">The customer paid <strong>${order.amount}</strong>. How would you like to resolve it?</p>
            <button onClick={() => setChoice("refund")} data-testid="cancel-choice-refund"
              className={`w-full rounded-xl border p-3 flex items-center gap-3 text-left transition-all ${choice === "refund" ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 hover:border-blue-300"}`}>
              <RotateCcw className="w-5 h-5 text-blue-600" />
              <div><div className="font-semibold text-sm text-slate-800">Refund to card</div><div className="text-xs text-slate-500">Send ${order.amount} back via Stripe</div></div>
            </button>
            <button onClick={() => hasAccount && setChoice("credit")} data-testid="cancel-choice-credit" disabled={!hasAccount}
              className={`w-full rounded-xl border p-3 flex items-center gap-3 text-left transition-all ${!hasAccount ? "border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed" : choice === "credit" ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 hover:border-blue-300"}`}>
              <Wallet className="w-5 h-5 text-green-600" />
              <div><div className="font-semibold text-sm text-slate-800">Store credit</div><div className="text-xs text-slate-500">{hasAccount ? `Add $${order.amount} to their account` : "Guest order — no account for credit"}</div></div>
            </button>
          </div>
        ) : (
          <p className="text-sm text-slate-600 mb-5">This order hasn't been paid, so there's nothing to refund. Cancelling frees up the delivery slot.</p>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50" data-testid="cancel-modal-back-btn">Keep order</button>
          <button onClick={confirm} disabled={loading} data-testid="cancel-modal-confirm-btn"
            className="flex-1 py-3 rounded-full bg-red-600 text-white font-semibold text-sm hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel order"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddDriverModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password required"); return; }
    setLoading(true);
    try {
      await api.post("/admin/drivers", form);
      toast.success("Driver added");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-5" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="add-driver-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-lg text-slate-900">Add Driver</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          {[
            { k: "name", label: "Full name", type: "text", tid: "driver-name-input" },
            { k: "email", label: "Email", type: "email", tid: "driver-email-input" },
            { k: "phone", label: "Phone", type: "text", tid: "driver-phone-input" },
            { k: "password", label: "Password", type: "text", tid: "driver-password-input" },
          ].map((f) => (
            <div key={f.k}>
              <label className="text-xs font-medium text-slate-600 mb-1 block">{f.label}</label>
              <input type={f.type} value={form[f.k]} onChange={set(f.k)} data-testid={f.tid}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm" />
            </div>
          ))}
        </div>
        <button onClick={submit} disabled={loading}
          className="brand-gradient btn-shine text-white font-semibold w-full py-3 rounded-full mt-5 flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="save-driver-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Add Driver"}
        </button>
      </div>
    </div>
  );
}
